import { useContext, useEffect, FC } from 'react';
import { useIntl } from 'dibs-react-intl';
import { useRelayEnvironment, useFragment, graphql } from 'react-relay';
import { datadogRum } from '@datadog/browser-rum';
import { setUpCheckout } from '../../../actions/checkoutSetUpActions';
import { setPreRenderState } from '../../../actions/checkoutUIActions';
import { getCurrency } from 'dibs-intl/exports/units';
import { useKlarnaSessionSync } from '../../../helpers/klarna';
import { useTrackCheckoutLoadEvent } from '../../../helpers/useTrackCheckoutLoadEvent';
import { useCheckoutSelector, useAppDispatch, useCheckoutStore } from '../../../reducers/root';

import { usePrerenderedCheckoutStore } from '../../../prerender/prerenderedCheckoutStoreReact';
import { CheckoutServerVarsContext } from '../../shared/CheckoutServerVarsContext';
import { usePrevious } from 'dibs-react-hooks/exports/usePrevious';
import { PreloadedDuties } from '../../../delivery/usePreloadedDuties';
import { useCheckoutPrePageview } from '../hooks/useCheckoutPrePageview';

// components
import { CheckoutLayout } from '../../../CheckoutLayout';
import CheckoutProcessingPage from '../CheckoutProcessingPage';
import { multiStageCheckoutTransactionQuery } from '../../../queries/multiStageCheckoutTransaction';
import { multiStageCheckoutTransactionQuery as multiStageCheckoutTransactionQueryType } from '../../../queries/__generated__/multiStageCheckoutTransactionQuery.graphql';

import { useRendererQueryLoader } from './useRendererQueryLoader';
import { usePartialAddressTracking } from '../../../hooks/usePartialAddressTracking';

import { CheckoutTransactionQueryRenderer_viewer$key } from './__generated__/CheckoutTransactionQueryRenderer_viewer.graphql';
import { CheckoutTransactionQueryRenderer_item$key } from './__generated__/CheckoutTransactionQueryRenderer_item.graphql';
import { CheckoutTransactionQueryRenderer_buyer$key } from './__generated__/CheckoutTransactionQueryRenderer_buyer.graphql';
import { CheckoutTransactionQueryRenderer_deliveryOptions$key } from './__generated__/CheckoutTransactionQueryRenderer_deliveryOptions.graphql';
import { CheckoutTransactionQueryRenderer_preCheckoutTransaction$key } from './__generated__/CheckoutTransactionQueryRenderer_preCheckoutTransaction.graphql';

// helpers
import { getUserSessionCountryCode } from 'dibs-regional-info/exports/regionalInfoHelpers';
import { getFdfQuoteABTestVariant } from 'dibs-buyer-layout/src/helpers/getFdfQuoteABTestVariant';
import { useTransactionRecomputed } from '../hooks/useTransactionRecomputed';

const viewerFragment = graphql`
    fragment CheckoutTransactionQueryRenderer_viewer on Viewer {
        ...CheckoutLayout_viewer
    }
`;
const itemFragment = graphql`
    fragment CheckoutTransactionQueryRenderer_item on Item {
        ...CheckoutLayout_item
        ...CheckoutProcessingPage_item
        ...checkoutSetUpActions_item
    }
`;
const buyerFragment = graphql`
    fragment CheckoutTransactionQueryRenderer_buyer on User {
        ...CheckoutLayout_buyer
        ...checkoutSetUpActions_buyer
        ...usePartialAddressTracking_buyer
        ...useTrackCheckoutLoadEvent_buyer
    }
`;
const deliveryOptionsFragment = graphql`
    fragment CheckoutTransactionQueryRenderer_deliveryOptions on ShipmentQuote
    @relay(plural: true) {
        ...CheckoutLayout_deliveryOptions
    }
`;
const preCheckoutTransactionFragment = graphql`
    fragment CheckoutTransactionQueryRenderer_preCheckoutTransaction on Transaction {
        ...CheckoutLayout_preCheckoutTransaction
        __typename
    }
`;

type Props = {
    viewer: CheckoutTransactionQueryRenderer_viewer$key | null;
    item: CheckoutTransactionQueryRenderer_item$key | null;
    buyer: CheckoutTransactionQueryRenderer_buyer$key | null;
    deliveryOptions: CheckoutTransactionQueryRenderer_deliveryOptions$key | null;
    preCheckoutTransaction: CheckoutTransactionQueryRenderer_preCheckoutTransaction$key | null;
    preloadedDuties: PreloadedDuties;
};

const CheckoutTransactionQueryRenderer: FC<Props> = ({
    viewer: viewerRef,
    item: itemRef,
    buyer: buyerRef,
    deliveryOptions: deliveryOptionsRef,
    preCheckoutTransaction: preCheckoutTransactionRef,
    preloadedDuties,
}) => {
    const intl = useIntl();
    const defaultCurrency = getCurrency(intl.locale);
    const { isMobile } = useContext(CheckoutServerVarsContext);

    const transactionType = useCheckoutSelector(state => state.transaction.transactionType);
    const transactionId = useCheckoutSelector(state => state.transaction.transactionId || null);
    const itemId = useCheckoutSelector(state => state.transaction.itemId);
    const buyerId = useCheckoutSelector(state => state.transaction.buyerId);
    const skuId = useCheckoutSelector(state => state.transaction.skuId);
    const buyerChargeCurrency = useCheckoutSelector(state => state.transaction.buyerChargeCurrency);
    const shipmentType = useCheckoutSelector(state => state.transaction.shipmentType);
    const shipmentQuoteId = useCheckoutSelector(state => state.transaction.shipmentQuoteId);
    const shippingAddressId = useCheckoutSelector(
        // use previousShippingAddressId until new delivery options are updated in the store
        state => state.transaction.previousShippingAddressId || state.transaction.shippingAddressId
    );
    const quantity = useCheckoutSelector(state => state.transaction.quantity);
    const promoCode = useCheckoutSelector(state => state.transaction.promoCode);
    const offerAmountPerItem = useCheckoutSelector(state => state.transaction.offerAmountPerItem);
    const offerCurrency = useCheckoutSelector(state => state.transaction.offerCurrency);
    const currencyConversionRateEventId = useCheckoutSelector(
        state => state.transaction.currencyConversionRateEventId
    );
    const paymentMethodApiType = useCheckoutSelector(
        state => state.transaction.paymentMethodApiType
    );
    const tradeStatus = useCheckoutSelector(state => state.tradeStatus);
    const isReadyAfterDeliveryOptionsFetch = useCheckoutSelector(
        state => state.ui.isReadyAfterDeliveryOptionsFetch
    );
    const isDeliveryOptionsComputing = useCheckoutSelector(
        state =>
            !!state.transaction.previousShippingAddressId &&
            state.transaction.previousShippingAddressId !== state.transaction.shippingAddressId
    );
    const zipCode = useCheckoutSelector(state => state.shipping.defaultZipCode);
    const country = useCheckoutSelector(state => state.shipping.defaultCountry);
    const isSelectedAddressValid = useCheckoutSelector(
        state => state.shipping.isSelectedAddressValid
    );
    const isCustomQuote = useCheckoutSelector(state => state.delivery.isCustomQuoteSelected);
    const isTaxExempt = useCheckoutSelector(state => state.transaction.isTaxExempt);
    const billingAddress = useCheckoutSelector(state => state.transaction.billingAddress);
    const isVatReverseCharge = useCheckoutSelector(state => state.transaction.isVatReverseCharge);
    const isProcessing = useCheckoutSelector(state => state.ui.isProcessing);
    const initialPane = useCheckoutSelector(state => state.pane.initialPane);
    const isCheckoutReady = useCheckoutSelector(state => state.ui.isCheckoutReady);
    const avalaraTaxExemptCertificateId = useCheckoutSelector(
        state => state.ui.avalaraTaxExemptCertificateId
    );
    const hasAvailableOptions = useCheckoutSelector(state => state.transaction.hasAvailableOptions);

    const isUpdatingKlarnaSession = useKlarnaSessionSync(transactionId);

    const store = useCheckoutStore();

    const viewer = useFragment(viewerFragment, viewerRef);
    const item = useFragment(itemFragment, itemRef);
    const buyer = useFragment(buyerFragment, buyerRef);
    const deliveryOptions = useFragment(deliveryOptionsFragment, deliveryOptionsRef);
    const preCheckoutTransaction = useFragment(
        preCheckoutTransactionFragment,
        preCheckoutTransactionRef
    );

    const [fetchQuery, data, loadingState] =
        useRendererQueryLoader<multiStageCheckoutTransactionQueryType>(
            multiStageCheckoutTransactionQuery
        );

    const transactionQueryViewer = data?.viewer || null;
    const computeTransactionMultiStageCheckout =
        transactionQueryViewer?.computeTransactionMultiStageCheckout;
    const transaction = computeTransactionMultiStageCheckout?.transaction;
    const checkoutPageViewTracking = transaction?.checkoutPageViewTracking;
    const isLoading = loadingState === 'loading';
    const previousShippingAddressId = usePrevious(shippingAddressId);
    const isComputing =
        isLoading ||
        isDeliveryOptionsComputing ||
        previousShippingAddressId !== shippingAddressId ||
        isUpdatingKlarnaSession;

    const environment = useRelayEnvironment();
    const dispatch = useAppDispatch();

    const { rootState } = usePrerenderedCheckoutStore();
    const trackCheckoutLoadEvent = useTrackCheckoutLoadEvent({
        buyer,
        transaction: transaction || null,
    });

    const fdfQuoteABTestVariant = getFdfQuoteABTestVariant();

    useEffect(() => {
        dispatch(setPreRenderState(rootState));
    }, [rootState, dispatch]);

    const previousData = usePrevious(data);
    useEffect(() => {
        if (transactionQueryViewer && data !== previousData) {
            const hasSetupCheckout = store.getState().ui.hasSetUpCheckout;
            if (!hasSetupCheckout && !isComputing && item && transaction) {
                setUpCheckout(
                    {
                        viewer: transactionQueryViewer,
                        item,
                        buyer,
                        transaction,
                        environment,
                        isMobile,
                        checkoutPageViewTracking,
                        initialPane,
                        country,
                        isSelectedAddressValid,
                    },
                    dispatch
                );

                //fire checkout load event

                trackCheckoutLoadEvent();

                datadogRum.addAction('checkout_loaded');
                if (
                    typeof window !== 'undefined' &&
                    (window as $TSFixMe).__DIBS_CHECKOUT_START_TIME__
                ) {
                    // we saw greatly increased checkout_loaded_t times after we
                    // turned pre-render back on (11/7), so the hypothesis is that DD is
                    // measuring the time from when the page is loaded, not when
                    // the checkout route changes in pre-render. This is to control for that
                    // vs checkout_loaded_time
                    datadogRum.addTiming(
                        'checkout_loaded_t',
                        Date.now() - (window as $TSFixMe).__DIBS_CHECKOUT_START_TIME__
                    );
                }
            }
        }
    }, [
        buyer,
        buyerId,
        checkoutPageViewTracking,
        data,
        dispatch,
        environment,
        initialPane,
        isComputing,
        isMobile,
        item,
        previousData,
        store,
        transaction,
        transactionQueryViewer,
        country,
        isSelectedAddressValid,
        shipmentQuoteId,
        shipmentType,
        shippingAddressId,
        isCustomQuote,
        trackCheckoutLoadEvent,
        fdfQuoteABTestVariant,
    ]);

    useCheckoutPrePageview();
    usePartialAddressTracking({ buyer });

    useEffect(() => {
        if (
            !!shipmentType &&
            !!itemId &&
            isReadyAfterDeliveryOptionsFetch &&
            isCheckoutReady &&
            !isDeliveryOptionsComputing
        ) {
            fetchQuery({
                input: {
                    transactionType,
                    transactionId,
                    itemId: itemId || '',
                    buyerId,
                    buyerChargeCurrency: buyerChargeCurrency || defaultCurrency,
                    isTaxExempt,
                    shipmentType,
                    shipmentQuoteId,
                    shippingAddressId,
                    isCustomQuote,
                    skuId,
                    quantity,
                    promoCode,
                    offerAmountPerItem,
                    offerCurrency,
                    currencyConversionRateEventId,
                    paymentMethodApiType,
                    zipCode,
                    country,
                    defaultShipping: true,
                    billingAddress,
                    isVatReverseCharge,
                    fdfQuoteABTestVariant,
                },
                itemId: itemId || '',
                buyerId,
                userIds: [buyerId],
                hasUserId: !!buyerId,
                hasAvailableOptions,
                /**
                 * When `hasSetUpCheckout` changes from `false` to `true` we have to refetch the whole `multiStageCheckoutTransactionQuery`. This causes us to always fetch it twice on startup.
                 *
                 * We work around this by just pulling `hasSetUpCheckout` from the store directly.
                 *
                 */
                hasSetUpCheckout: store.getState().ui.hasSetUpCheckout,
                hasTradeStatus: tradeStatus.hasTradeStatus,
                isMobile,
                displayCurrency: offerCurrency,
                userCountryCode: getUserSessionCountryCode(),
                fdfQuoteABTestVariant,
            });
        }
    }, [
        billingAddress,
        buyerChargeCurrency,
        buyerId,
        country,
        currencyConversionRateEventId,
        defaultCurrency,
        fetchQuery,
        store,
        isCustomQuote,
        isMobile,
        isTaxExempt,
        isVatReverseCharge,
        itemId,
        offerAmountPerItem,
        offerCurrency,
        paymentMethodApiType,
        promoCode,
        quantity,
        shipmentQuoteId,
        shipmentType,
        shippingAddressId,
        skuId,
        tradeStatus.hasTradeStatus,
        transactionId,
        transactionType,
        zipCode,
        hasAvailableOptions,
        deliveryOptions,
        isReadyAfterDeliveryOptionsFetch,
        isCheckoutReady,
        isDeliveryOptionsComputing,
        fdfQuoteABTestVariant,
        // this is used to refetch transaction after a certificate is uploaded to Avalara via SDK
        avalaraTaxExemptCertificateId,
    ]);

    useTransactionRecomputed({ transaction: transaction || null });

    if (isProcessing && item) {
        return <CheckoutProcessingPage item={item} />;
    }

    return (
        <CheckoutLayout
            inlineSummary={isMobile}
            viewer={viewer || null}
            item={item}
            buyer={buyer}
            transaction={transaction || null}
            deliveryOptions={deliveryOptions}
            preCheckoutTransaction={preCheckoutTransaction}
            isComputing={isComputing}
            preloadedDuties={preloadedDuties}
        />
    );
};

export default CheckoutTransactionQueryRenderer;
